import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Form, Button, InputGroup } from 'react-bootstrap';
import { useLazyQuery, useMutation } from '@apollo/client';
import { CREATE_SETTING } from '../../../apollo/setting_apollo/mutation';
import { GET_SETTING_BY_SHOP } from '../../../apollo/setting_apollo/query';
import { useLocation } from 'react-router-dom/cjs/react-router-dom';
import LoadingUpdates from '../../../components/LoadingUpdate';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { SHOP_SETTING_KEY } from '../../../const';
import { errorSwal } from '../../../helper/sweetalert';

export default function ShopSettingManagement() {
  const location = useLocation();
  const history = useHistory()

  const [isEditMode, setIsEditMode] = useState(false);
  const [tax, setTax] = useState('');
  const [taxStatus, setTaxStatus] = useState(false);
  const [taxType, setTaxType] = useState("PERCENT");
  const [isVatIncluded, setIsVatIncluded] = useState(true);
  const [settingObject, setSettingObject] = useState({});
  const [stockInOut, setStockInOut] = useState(false);
  const [commissionEnable, setCommissionEnable] = useState(false);
  const [shippingCost, setShippingCost] = useState(false);
  const [splitAmountOrder, setSplitAmountOrder] = useState(false);
  const [printBillA4, setPrintBillA4] = useState(false);
  const [editPriceBeforeSale, setEditPriceBeforeSale] = useState(false);
  const [editOrderAfterSale, setEditOrderAfterSale] = useState(false);
  const [loyaltyEnable, setLoyaltyEnable] = useState(true);
  const sampleAmount = 10000;
  const taxRateValue = parseFloat(tax || 0) || 0;
  const isPercent = taxType === "PERCENT";
  const includedTaxAmount = isPercent
    ? (taxRateValue > 0 ? (sampleAmount * taxRateValue) / (100 + taxRateValue) : 0)
    : taxRateValue;
  const includedPriceBeforeTax = Math.max(0, sampleAmount - includedTaxAmount);
  const excludedTaxAmount = isPercent ? (sampleAmount * taxRateValue) / 100 : taxRateValue;
  const excludedTotal = sampleAmount + excludedTaxAmount;
  const formatExample = (value) => new Intl.NumberFormat("en-US").format(Math.round(value * 100) / 100);

  const [createSetting, { loading: loadingCreating }] = useMutation(CREATE_SETTING);
  const [loadSettingShop, { data: settingShop, loading: loadingQuery }] = useLazyQuery(GET_SETTING_BY_SHOP, { fetchPolicy: "network-only" });

  // user data from local storage
  const USER_DATA = localStorage.getItem("USER_DATA");
  const role = JSON.parse(USER_DATA).data.role
  


  // Effect hook to load setting data when the component mounts
  useEffect(() => {
    if (location?.state?.shop?.id) {
      querySettingShop();
    }
  }, [location]);

  useEffect(() => {
    if (settingShop) {
      const { taxes } = settingShop?.settingByShopId || {};
      setSettingObject(settingShop?.settingByShopId);
      setTax(taxes?.tax || '');
      setTaxStatus(taxes?.taxStatus || false);
      setTaxType(taxes?.taxType || 'PERCENT');
      setIsVatIncluded(taxes?.isVatIncluded ?? true);
      setStockInOut(settingShop?.settingByShopId?.stockInOut || false);
      setCommissionEnable(settingShop?.settingByShopId?.commissionEnable ?? false);
      setShippingCost(settingShop?.settingByShopId?.shippingCost || false);
      setSplitAmountOrder(settingShop?.settingByShopId?.splitAmountOrder || false);
      setPrintBillA4(settingShop?.settingByShopId?.printBillA4 || false);
      setEditPriceBeforeSale(settingShop?.settingByShopId?.editPriceBeforeSale || false);
      setEditOrderAfterSale(settingShop?.settingByShopId?.editOrderAfterSale || false);
      setLoyaltyEnable(settingShop?.settingByShopId?.loyaltyEnable ?? true);
      // save to local storage
      localStorage.setItem(SHOP_SETTING_KEY, JSON.stringify({
        ...settingShop?.settingByShopId,
        commissionEnable: settingShop?.settingByShopId?.commissionEnable ?? false
      }));
    }
  }, [settingShop]);

  // Function to handle setting creation/updating
  const createSettingShop = async () => {
    if (taxStatus && tax <= 0) {
      return errorSwal("ຖ້າຕ້ອງການນຳໃຊ້ພາສີອາກອນ ກະລຸນາປ້ອນ ຈຳນວນກ່ອນກົດບັນທືກ");
    }
    try {
      const response = await createSetting({
        variables: {
          data: {
            taxes: {
              tax: tax,
              taxStatus: taxStatus,
              taxType: taxType,
              isVatIncluded: isVatIncluded,
            },
            shop: location?.state?.shop?.id,
            stockInOut: stockInOut,
            commissionEnable: commissionEnable,
            shippingCost: shippingCost,
            splitAmountOrder: splitAmountOrder,
            printBillA4: printBillA4,
            editPriceBeforeSale: editPriceBeforeSale,
            editOrderAfterSale: editOrderAfterSale,
            loyaltyEnable: loyaltyEnable,
          },
        },
      });
      if (response?.data?.createSetting?.id) {
        await querySettingShop(); // Re-fetch settings after creation/update
        setIsEditMode(false);
      }
    } catch (error) {
      console.error("Error saving setting:", error);
    }
  };

  // Function to fetch shop settings
  const querySettingShop = async () => {
    if (loadingQuery) return;
    try {
      const response = await loadSettingShop({
        variables: {
          where: { shop: location?.state?.shop?.id },
        },
      });
      localStorage.setItem(SHOP_SETTING_KEY, JSON.stringify({
        ...response?.data?.settingByShopId,
        commissionEnable: response?.data?.settingByShopId?.commissionEnable ?? false
      }));
    } catch (error) {
      console.error("Error fetching setting:", error);
    }
  };

  return (
    <React.Fragment>
      <Card className="p-4 min-h-screen">
        <Card.Header>
          <Card.Title>ຈັດການຕັ້ງຄ່າຮ້ານ</Card.Title>
        </Card.Header>
        <Card.Body>
          <Card.Body className="p-0">
            {/* Tax settings */}
            <Row className="align-items-start p-3 border-bottom">
              <Col md={6}>
                <Form.Label className="fw-bold"><b>ພາສີອາກອນ</b></Form.Label>
              </Col>
              <Col md={6}>
                <Form.Group controlId="exampleForm.ControlInput1">
                  <Form.Label>ປ້ອນຈຳນວນ</Form.Label>
                  <InputGroup className="mb-3">
                      <Form.Control
                        type="text"
                        value={tax}
                        onChange={(e) => setTax(e?.target?.value)}
                      />
                    <InputGroup.Text id="basic-addon2">{taxType === "PERCENT" ? "%": "ຈຳນວນເຕີມ"}</InputGroup.Text>
                  </InputGroup>
                </Form.Group>
                <div className="mt-3 d-flex gap-4 justify-content-start align-items-center">
                  <Form.Check
                    type="radio"
                    id="allow-products"
                    label="ເປີດນຳໃຊ້"
                    name="taxStatus"
                    checked={taxStatus}
                    onChange={() => setTaxStatus(true)}
                  />
                  <Form.Check
                    type="radio"
                    id="allow-products"
                    label="ປີດນຳໃຊ້"
                    name="taxStatus"
                    checked={!taxStatus}
                    onChange={() => setTaxStatus(false)}
                  />
                  <div style={{ width: 40 }} />
                  <Form.Check
                    type="radio"
                    id="allow-products"
                    label="ຄິດໄລ່ເປັນເປີເຊັນ"
                    name="taxType"
                    checked={taxType === "PERCENT"}
                    onChange={() => setTaxType("PERCENT")}
                  />
                  <Form.Check
                    type="radio"
                    id="allow-products"
                    label="ຄິດໄລ່ເປັນເງິນ"
                    name="taxType"
                    checked={taxType === "FIXED"}
                    onChange={() => setTaxType("FIXED")}
                  />
                </div>
                <div className="mt-3 d-flex gap-4 justify-content-start align-items-center">
                  <Form.Check
                    type="radio"
                    id="vat-included"
                    label="ລວມ VAT"
                    name="vatIncluded"
                    checked={isVatIncluded === true}
                    onChange={() => setIsVatIncluded(true)}
                  />
                  <Form.Check
                    type="radio"
                    id="vat-excluded"
                    label="ແຍກ VAT"
                    name="vatIncluded"
                    checked={isVatIncluded === false}
                    onChange={() => setIsVatIncluded(false)}
                  />
                </div>
                <div className="mt-2" style={{color:"gray"}}>
                  <div style={{ fontSize: 13 }}>
                    ຕົວຢ່າງລວມ VAT: ລາຄາລວມ {formatExample(sampleAmount)} ₭, VAT {isPercent ? `${taxRateValue}%` : `${formatExample(taxRateValue)} ₭`} = {formatExample(includedTaxAmount)} ₭, ກ່ອນ VAT = {formatExample(includedPriceBeforeTax)} ₭
                  </div>
                  <div style={{ fontSize: 13 }}>
                    ຕົວຢ່າງແຍກ VAT: ລາຄາກ່ອນ VAT {formatExample(sampleAmount)} ₭, VAT {isPercent ? `${taxRateValue}%` : `${formatExample(taxRateValue)} ₭`} = {formatExample(excludedTaxAmount)} ₭, ລວມທັງໝົດ = {formatExample(excludedTotal)} ₭
                  </div>
                </div>
              </Col>
            </Row>

            <Row className="align-items-center p-3 border-bottom">
              <Col md={6}>
                <Form.Label className="fw-bold"><b>Phaypay ການຊຳລະຜ່ານ dynamic qr code (ກຳລັງພັດທະນາ)</b></Form.Label>
              </Col>
              <Col md={6}>
                <Form.Select disabled defaultValue="percentage" className="mb-2" style={{ width: '250px' }}>
                  <option value="percentage">Percentage</option>
                  <option value="fixed">Fixed</option>
                </Form.Select>
                <Form.Text className="text-muted">Select the commission type</Form.Text>
              </Col>
            </Row>

            {/* Admin commission */}
            <Row className="align-items-center p-3 border-bottom">
              <Col md={6}>
                <Form.Label className="fw-bold">ຮັບແບບການຂາຍ (ກຳລັງພັດທະນາ)</Form.Label>
              </Col>
              <Col md={6}>
                <Form.Select disabled defaultValue="10" className="mb-2" style={{ width: '250px' }}>
                  <option value="5">5%</option>
                  <option value="10">10%</option>
                  <option value="15">15%</option>
                  <option value="20">20%</option>
                </Form.Select>
                <Form.Text className="text-muted">Amount you get from sales</Form.Text>
              </Col>
            </Row>

            {/* Stock in/out */}
            <Row className="align-items-center p-3 border-bottom">
              <Col md={6}>
                <Form.Label className="fw-bold">ຄິດໄລ່ຄ່າຄອມມິດຊັ່ນ(*)</Form.Label>
              </Col>
              <Col md={6}>
                <Form.Check
                  type="switch"
                  id="commissionEmployee"
                  label="ເປີດ/ປີດ ຄິດໄລ່ຄ່າຄອມມິດຊັ່ນຕໍ່ສິນຄ້າ"
                  checked={commissionEnable}
                  onChange={(e) => setCommissionEnable(e?.target?.checked)}
                />
              </Col>
            </Row>
            <Row className="align-items-center p-3 border-bottom">
              <Col md={6}>
                <Form.Label className="fw-bold">ສີນຄ້າເຂົ້າອອກ(*)</Form.Label>
              </Col>
              <Col md={6}>
                <Form.Check
                  type="switch"
                  id="stock-in-out"
                  label="ເປີດ/ປີດ ການຈັດການສີນຄ້າເຂົ້າອອກ"
                  checked={stockInOut}
                  onChange={(e) => setStockInOut(e?.target?.checked)}
                />
              </Col>
            </Row>
            {/* Stock in/out */}
            <Row className="align-items-center p-3 border-bottom">
              <Col md={6}>
                <Form.Label className="fw-bold">ແຍກຈຳນວນອໍເດີ(*)</Form.Label>
              </Col>
              <Col md={6}>
                <Form.Check
                  type="switch"
                  id="splitAmountOrder"
                  label="ເປີດ/ປີດ ການຈັດການແຍກຈຳນວນຂອງອໍເດີ"
                  checked={splitAmountOrder}
                  onChange={(e) => setSplitAmountOrder(e?.target?.checked)}
                />
              </Col>
            </Row>

            {/* Print bill A4 */}
            <Row className="align-items-center p-3 border-bottom">
              <Col md={6}>
                <Form.Label className="fw-bold">ພີມບີນA4(*)</Form.Label>
              </Col>
              <Col md={6}>
                <Form.Check
                  type="switch"
                  id="printBillA4"
                  label="ເປີດ/ປີດ ການຈັດການພີມບີນA4"
                  checked={printBillA4}
                  onChange={(e) => setPrintBillA4(e?.target?.checked)}
                />
              </Col>
            </Row>

            {/* Enable & disable edit bill after sale */}
            <Row className="align-items-center p-3 border-bottom">
              <Col md={6}>
                <Form.Label className="fw-bold">ການແກ້ໄຂບິນຫຼັງການຂາຍ(*)</Form.Label>
              </Col>
              <Col md={6}>
                <Form.Check
                  type="switch"
                  id="editOrderAfterSale"
                  label="ເປີດ/ປີດ ການແກ້ໄຂບິນຫຼັງການຂາຍ ເລືອກເປີດເພື່ອບ໊ອກການແກ້ໄຂບິນ"
                  disabled={role === "ADMIN" ? false : true}
                  checked={editOrderAfterSale}
                  onChange={(e) => setEditOrderAfterSale(e?.target?.checked)}
                />
              </Col>
            </Row>

            {/* Enable & disable price before sale */}
            <Row className="align-items-center p-3 border-bottom">
              <Col md={6}>
                <Form.Label className="fw-bold">ການແກ້ໄຂລາຄາກ່ອນການຂາຍ(*)</Form.Label>
              </Col>
              <Col md={6}>
                <Form.Check
                  type="switch"
                  id="editPriceBeforeSale"
                  label="ເປີດ/ປີດ ການແກ້ໄຂລາຄາກ່ອນການຂາຍ ເລືອກເປີດເພື່ອບ໊ອກການແກ້ໄຂລາຄາ"
                  disabled={role === "ADMIN" ? false : true}
                  checked={editPriceBeforeSale}
                  onChange={(e) => setEditPriceBeforeSale(e?.target?.checked)}
                />
              </Col>
            </Row>

            {/* Stock shipping cost */}
            <Row className="align-items-center p-3 border-bottom">
              <Col md={6}>
                <Form.Label className="fw-bold">ຄ່າຂົນສົ່ງ(*)</Form.Label>
              </Col>
              <Col md={6}>
                <Form.Check
                  type="switch"
                  id="shipping-cost"
                  label="ເປີດ/ປີດ ການຈັດການຄ່າຂົນສົ່ງສີນຄ້າ"
                  checked={shippingCost}
                  onChange={(e) => setShippingCost(e?.target?.checked)}
                />
              </Col>
            </Row>

            <Row className="align-items-center p-3 border-bottom">
              <Col md={6}>
                <Form.Label className="fw-bold">ເລີ່ມສະສົມຄະແນນຂອງລູກຄ້າ(*)</Form.Label>
              </Col>
              <Col md={6}>
                <Form.Check
                  type="switch"
                  id="loyalty-enable"
                  label="ເປີດ/ປີດ ການຄຳນວນຄະແນນ"
                  checked={loyaltyEnable}
                  onChange={(e) => setLoyaltyEnable(e?.target?.checked)}
                />
              </Col>
            </Row>

            {/* Order status change */}
            <Row className="align-items-center p-3 border-bottom">
              <Col md={6}>
                <Form.Label className="fw-bold">ກຳນົດເລດເງິນ (ກຳລັງພັດທະນາ)</Form.Label>
              </Col>
              <Col md={6}>
                <Form.Check
                  type="switch"
                  id="vendor-update-status"
                  label="Vendor can update order status"
                  defaultChecked
                  disabled
                />
              </Col>
            </Row>

            {/* Disable product popup */}
            <Row className="align-items-center p-3 border-bottom">
              <Col md={6}>
                <Form.Label className="fw-bold">ກຳນົດຄຳອະທິບາຍຂອບໃຈກ້ອງບິນຂາຍ</Form.Label>
              </Col>
              <Col md={6}>
                <Form.Control
                  type="text"
                  id="disable-product-popup"
                  disabled
                />
              </Col>
            </Row>

            <div className="d-flex gap-3 justify-content-end w-100 mt-5">
              <Button variant="outline-secondary" onClick={() => history.goBack(-1)}>
             ຍົກເລີກ
              </Button>
                <Button variant="success" onClick={createSettingShop} disabled={loadingCreating}>
                  {loadingCreating ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກຂໍ້ມູນ"}
                </Button>
            </div>
          </Card.Body>
        </Card.Body>
      </Card>

      <LoadingUpdates title="ກຳລັງອັບເດດ..." openLoading={loadingCreating} />

    </React.Fragment>
  );
}
